// Pure decision helpers for the short-pick flow on the pick screen.
//
// A short pick is an operator confirming fewer units than the pick task asked
// for. The confirmed quantity is recorded against the task
// (PUT /PickStation/updatePickTask/{operatorTaskId}/{actualQuantity}) and the
// bin+SKU entry is flagged with the reason, before the pick is signalled as normal — see
// SkuBin.handleShortPickSubmit.
//
// The reason list is closed (no free text) and deliberately mirrors the
// "Report a Problem" SKU-flagging list, plus "Insufficient Quantity" which only
// makes sense for a short pick. `remarks` is what the flag endpoint stores, so
// the first two reuse the exact strings the flag flow has always sent.

export type ShortPickReason = "damaged" | "wrong" | "insufficient";

export interface ShortPickReasonOption {
  value: ShortPickReason;
  label: string;
  /** Remarks recorded against the bin+SKU entry by /BinMaster/flagBinSku. */
  remarks: string;
}

export const SHORT_PICK_REASONS: ShortPickReasonOption[] = [
  { value: "damaged", label: "Damaged SKU / Bin", remarks: "Damaged SKU-Bin" },
  { value: "wrong", label: "Wrong SKU / Bin", remarks: "Wrong SKU-Bin" },
  {
    value: "insufficient",
    label: "Insufficient Quantity",
    remarks: "Insufficient Quantity",
  },
];

export const getShortPickRemarks = (
  reason: ShortPickReason | null
): string | null =>
  SHORT_PICK_REASONS.find((option) => option.value === reason)?.remarks ?? null;

/**
 * True when the operator is confirming fewer units than the task requires.
 *
 * Both quantities are coerced defensively: the pick task quantity arrives from
 * the API and the picked quantity from component state, and either can be a
 * string. Anything non-finite is treated as "not a short pick" so a parse
 * failure can never divert a normal pick into the short-pick modal.
 */
export const isShortPick = (pickedQty: unknown, requiredQty: unknown): boolean => {
  const picked = Number(pickedQty);
  const required = Number(requiredQty);

  if (!Number.isFinite(picked) || !Number.isFinite(required)) {
    return false;
  }

  return picked < required;
};
